import { ReactNode } from 'react';
import { Stack, Theme, Typography, useTheme } from '@mui/material';

import {
  AssetsFilter,
  DateRangeFilter,
  FilterCheckbox,
  FilterItem as FilterItemType,
  FiltersBar,
  createTextChipRenderer,
  renderAssetsChips,
  renderDateRangeChip,
  useFiltersState,
} from 'src/components/streametric/filters-bar';
import { renderTitleIcon } from 'src/features/alarm-pop-up/utils';

type Props = {
  onChange: (filters: FilterItemType[]) => void;
  /** Rendered on the left of the filters row, e.g. pagination. */
  leading?: ReactNode;
};

const alarmStatusGroup = {
  name: 'alarm_status',
  label: 'Status',
};
const alarmTypeGroup = {
  name: 'alarm_type',
  label: 'Alarm type',
};
const acknowledgementGroup = {
  name: 'alarm_acknowledgement',
  label: 'Acknowledgement',
};
const assets = {
  name: 'assets',
  label: 'Assets',
};
const timeFrame = {
  name: 'time_frame',
  label: 'Time Frame',
};

const renderPriorityLabel = (
  htmlFor: string,
  priority: string,
  text: string,
  theme: Theme,
  color?: string
) => (
  <Stack component="label" htmlFor={htmlFor} flexDirection="row" alignItems="center">
    {renderTitleIcon({ priority } as Parameters<typeof renderTitleIcon>[0], theme, color)}
    <Typography sx={{ marginLeft: '4px' }} variant="body1">
      {text}
    </Typography>
  </Stack>
);

const createInitialFilters = (theme: Theme): FilterItemType[] => [
  {
    name: 'time_frame',
    label: 'Time Frame',
    component: DateRangeFilter,
    group: timeFrame,
    renderChip: renderDateRangeChip,
    value: '',
  },
  {
    name: 'active',
    component: FilterCheckbox,
    label: 'Active',
    value: false,
    group: alarmStatusGroup,
  },
  {
    name: 'cleared',
    component: FilterCheckbox,
    label: 'Cleared',
    value: false,
    group: alarmStatusGroup,
  },
  {
    name: 'Critical',
    component: FilterCheckbox,
    renderChip: createTextChipRenderer('Critical'),
    label: renderPriorityLabel('critical', 'Critical', 'Critical', theme, theme.palette.error.main),
    value: false,
    group: alarmTypeGroup,
  },
  {
    name: 'Warning',
    component: FilterCheckbox,
    renderChip: createTextChipRenderer('Warning'),
    label: renderPriorityLabel('warning', 'Warning', 'Warning', theme, theme.palette.warning.main),
    value: false,
    group: alarmTypeGroup,
  },
  {
    name: 'Info',
    component: FilterCheckbox,
    renderChip: createTextChipRenderer('Info'),
    label: renderPriorityLabel('info', 'Info', 'Info', theme),
    value: false,
    group: alarmTypeGroup,
  },
  {
    name: 'Offline',
    component: FilterCheckbox,
    renderChip: createTextChipRenderer('Communication'),
    label: renderPriorityLabel('info', 'Offline', 'Communication', theme),
    value: false,
    group: alarmTypeGroup,
  },
  {
    name: 'acknowledged',
    component: FilterCheckbox,
    label: 'Acknowledged',
    value: false,
    group: acknowledgementGroup,
  },
  {
    name: 'unacknowledged',
    label: 'Unacknowledged',
    component: FilterCheckbox,
    value: false,
    group: acknowledgementGroup,
  },
  {
    name: 'assets',
    label: 'Assets',
    component: AssetsFilter,
    value: [],
    group: assets,
    renderChip: renderAssetsChips,
  },
];

export const SiteAlarmFilters = ({ onChange, leading }: Props) => {
  const theme = useTheme();

  const { filtersState, appliedFilters, removeFilter, updateFilters, clearAll } = useFiltersState(
    () => createInitialFilters(theme),
    onChange
  );

  return (
    <FiltersBar
      filters={filtersState}
      appliedFilters={appliedFilters}
      onApply={updateFilters}
      removeFilter={removeFilter}
      onClearAll={clearAll}
      leading={leading}
    />
  );
};
